import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { show_error, show_success } from "lib/helpers"

// jobs/index_export.haml       data-controller="jobs--export"
// jobs/_export_schema.haml     nested inside the same controller root

export default class extends Controller {
  static targets = [
    "table", "classSelect", "exportBtnCsv", "exportBtnJson",
    "checkAll", "schemaModal", "selectAll", "schemaExportBtn",
  ]

  static values = {
    customClassesPath: String,
    checkStatusPath: String,
    getExportsPath: String,
    removeExportsPath: String,
    isReader: Boolean,
  }

  connect() {
    this.#initDataTable()
    this.#injectToolbar()
    this.#injectJobsLabel()
    this.#startOneTimeCheck()
    this.element.addEventListener("change", this.#handleTableCheckboxChange)
    this.element.addEventListener("click", this.#handleExportFileClick, true)
  }

  disconnect() {
    clearTimeout(this.timerOne)
    clearInterval(this.timerEvery)
    this.timerOne = null
    this.timerEvery = null

    this.element.removeEventListener("change", this.#handleTableCheckboxChange)
    this.element.removeEventListener("click", this.#handleExportFileClick, true)

    if (this.dataTable) {
      this.dataTable = destroyTurboDataTable(this.dataTable)
    }
  }

  // -- Actions ----------------------------------------------------------------

  classChanged() {
    const classId = this.classSelectTarget.value
    const url = `${this.customClassesPathValue}/export?class_id=${classId}`
    this.exportBtnCsvTarget.dataset.url = url
    this.exportBtnJsonTarget.dataset.url = url
  }

  exportCustomData(event) {
    event.preventDefault()
    const btn = event.currentTarget

    clearInterval(this.timerEvery)
    this.#startPeriodicCheck()

    $.ajax({
      url: btn.dataset.url,
      dataType: "JSON",
      data: { format: btn.dataset.format },
      success: (response) => {
        if (response.success) {
          show_success(response.message)
        } else {
          show_error(response.message)
        }
        this.dataTable.ajax.reload()
        this.dataTable.draw(false)
      },
    })
  }

  showSchemaModal() {
    $(this.schemaModalTarget).modal("show")
  }

  exportClassSchema(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const selected = Array.from(
      this.element.querySelectorAll(".select_field:checked"),
      (el) => el.value,
    )

    if (selected.length === 0) {
      alert("Custom Class for export are not selected!")
      return
    }

    $.ajax({
      url: btn.dataset.url,
      dataType: "JSON",
      data: { custom_class: selected },
      success: (response) => {
        if (response.success) {
          show_success(response.message)
        } else {
          show_error(response.message)
        }
        this.dataTable.ajax.reload()
        this.dataTable.draw(false)
      },
    })

    $(this.schemaModalTarget).modal("hide")
  }

  toggleAll() {
    const checked = this.checkAllTarget.checked
    this.element.querySelectorAll(".table-checkbox-costum-export").forEach((el) => {
      el.checked = checked
      el.closest("tr")?.classList.toggle("selected-row", checked)
    })
  }

  toggleAllSchema() {
    const checked = this.selectAllTarget.checked
    this.element.querySelectorAll(".select_field").forEach((el) => {
      el.checked = checked
    })
  }

  schemaFieldChanged() {
    const total = this.element.querySelectorAll(".select_field").length
    const checked = this.element.querySelectorAll(".select_field:checked").length
    this.selectAllTarget.checked = checked === total
  }

  removeSelected(event) {
    event.preventDefault()

    const $wrapper = $(this.tableTarget).closest(".dataTables_wrapper")
    const $checked = $wrapper.find(".table-checkbox-costum-export:checked")
    const $all = $wrapper.find(".table-checkbox-costum-export")

    if (!$all.length) return
    if (!$checked.length) { alert("Please select at least one item"); return }
    if (!confirm("Are you sure? All selected items will be removed")) return

    const allSelected = $checked.length === $all.length
    const ids = $checked.map(function () { return $(this).val() }).get()

    $wrapper.find(".dataTables_processing").css("display", "block")

    $.ajax({
      url: this.removeExportsPathValue,
      type: "DELETE",
      data: { item_ids: ids },
      success: () => {
        if (allSelected) {
          const settings = this.dataTable.settings()[0]
          if (settings._iRecordsDisplay - settings._iDisplayStart <= settings._iDisplayLength) {
            this.dataTable.page("previous")
          }
        }
        this.dataTable.draw(false)
      },
    })

    if (allSelected) this.checkAllTarget.click()
  }

  // -- Private ----------------------------------------------------------------

  #handleExportFileClick = (event) => {
    const link = event.target.closest("a.export-file-link, a[href*='/export_file/']")
    if (!link || !this.element.contains(link)) return

    event.preventDefault()
    event.stopPropagation()
    this.#downloadExportFile(link)
  }

  async #downloadExportFile(link) {
    try {
      const response = await fetch(link.href, {
        headers: { Accept: "application/octet-stream, application/json" },
      })
      const contentType = response.headers.get("content-type") || ""

      if (!response.ok) {
        let message = "Export file is not found. Run a new export."
        if (contentType.includes("application/json")) {
          const data = await response.json()
          if (data.error) message = data.error
        }
        alert(message)
        show_error(message)
        return
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const downloadLink = document.createElement("a")
      downloadLink.href = objectUrl
      downloadLink.download = link.textContent.trim()
      document.body.appendChild(downloadLink)
      downloadLink.click()
      downloadLink.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (_error) {
      const message = "Export file is not found. Run a new export."
      alert(message)
      show_error(message)
    }
  }

  #handleTableCheckboxChange = (event) => {
    const { target } = event
    if (!target.matches(".table-checkbox-costum-export")) return

    target.closest("tr")?.classList.toggle("selected-row", target.checked)

    const total = this.element.querySelectorAll(".table-checkbox-costum-export").length
    const selected = this.element.querySelectorAll(".table-checkbox-costum-export:checked").length
    this.checkAllTarget.checked = selected > 0 && selected === total
  }

  #initDataTable() {
    this.dataTable = createTurboDataTable(this.tableTarget, {
      dom: 'l<"jobs_label">rtip<"toolbar">',
      scrollX: true,
      pagingType: "full_numbers",
      processing: true,
      serverSide: true,
      order: [7, "desc"],
      ajax: this.getExportsPathValue,
      columnDefs: [
        {
          targets: 0,
          createdCell(cell, cellData) {
            $(cell).html(
              '<input id="check--dialogs ' + cellData + '" class="table-checkbox table-checkbox-costum-export" type="checkbox" name="item_ids[]" value="' + cellData + '"/> <label for="check--dialogs ' + cellData + '"></label>',
            )
          },
        },
        {
          targets: 6,
          createdCell(cell, cellData) {
            $(cell).html(cellData === "..." ? cellData : moment(cellData).format())
          },
        },
        {
          targets: 7,
          createdCell(cell, cellData) {
            $(cell).html(cellData ? moment(cellData).format() : "")
          },
        },
        { targets: [0, 1, 3, 4], sortable: false },
      ],
    })
  }

  #injectToolbar() {
    if (this.isReaderValue) return
    const toolbar = this.tableTarget.closest(".dataTables_wrapper")?.querySelector("div.toolbar")
    if (!toolbar) return
    toolbar.innerHTML =
      '<br/><br/><a href="#" class="remove" data-action="click->jobs--export#removeSelected">Remove selected</a>'
  }

  #injectJobsLabel() {
    const label = this.element.querySelector(".jobs_label")
    if (label) {
      label.innerHTML =
        '<label style="float:right; line-height: 26px; padding-right: 25px;"><strong></strong></label>'
    }
  }

  #startOneTimeCheck() {
    this.timerOne = setTimeout(() => this.#checkStatus("one"), 2000)
  }

  #startPeriodicCheck() {
    this.timerEvery = setInterval(() => this.#checkStatus("ever"), 10000)
  }

  #checkStatus(mode) {
    $.ajax({
      url: this.checkStatusPathValue,
      dataType: "JSON",
      success: (response) => {
        if (response.total_job > 1) {
          this.dataTable.ajax.reload()
          this.dataTable.draw(false)
        }
        if (response.status && mode === "ever") {
          this.dataTable.ajax.reload()
          this.dataTable.draw(false)
          clearInterval(this.timerEvery)
          this.timerEvery = null
        } else if (!response.status && mode === "one") {
          this.#startPeriodicCheck()
        }
      },
    })
  }
}
